import React, { useState, useEffect } from "react";
import { Auth, API } from "aws-amplify";
import "../config";
import "./Home.css";

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import AnmodningStatusPill from '../components/AnmodningStatusPill';

import Anmodninger from '../components/Anmodninger';
import AutoRefresh from '../components/AutoRefresh';
import LoadingWrapper from '../components/LoadingWrapper';
import { useSnackbar } from 'notistack';
import { useHistory } from "react-router-dom";
import { useContainerStyles } from '../styles/commonStyles';

const useStyles = makeStyles(theme => ({
  listItem: {
    borderBottom: '1px solid #e0e0e0',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      cursor: 'pointer',
    },
  },
  completedList: {
    maxHeight: '300px',
    overflow: 'auto',
  },
}));

export default function Home(props) {
  const defaultAnmodninger = {anmodninger: {type: "FeatureCollection", features: []}}
  const containerClasses = useContainerStyles();
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState([false,'']);
  const [anmodninger, setAnmodninger] = useState(defaultAnmodninger);
  const [searchQuery, setSearchQuery] = useState('');
  const history = useHistory();
  
  // Snackbar
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Extract properties from GeoJSON features
  const anmodningerList = anmodninger.anmodninger?.features?.map(feature => feature.properties) || [];

  // Filter anmodninger that are not "Gennemført"
  const activeAnmodninger = anmodningerList
    .filter(
      anmod => anmod.anmod_status !== 'Gennemført' && anmod.anmod_status !== 'Håndteret Manuelt'
    )
    .sort((a, b) => (b.graveforespoergselsnr || 0) - (a.graveforespoergselsnr || 0));

  // Filter anmodninger that are the opposite of above
  const completedAnmodninger = anmodningerList
    .filter(
      anmod => anmod.anmod_status === 'Gennemført' || anmod.anmod_status === 'Håndteret Manuelt'
    )
    .sort((a, b) => (b.graveforespoergselsnr || 0) - (a.graveforespoergselsnr || 0));

  // Filter completed anmodninger by search query
  const filteredCompletedAnmodninger = completedAnmodninger.filter(anmod => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const matchesGraveforespørgselsnr = anmod.graveforespoergselsnr && 
                                        anmod.graveforespoergselsnr.toString().includes(searchQuery);
    const matchesBemaerkning = anmod.bemaerkning && 
                               anmod.bemaerkning.toLowerCase().includes(query);
    return matchesGraveforespørgselsnr || matchesBemaerkning;
  });

  useEffect(() => {
    //console.log('vendorMeta change i setup')
    setIsLoading(true)
    if (props.vendorID === '') {
      setAnmodninger(defaultAnmodninger)
      setIsError([false, ''])
      setIsLoading(false)
    } else {
      setIsLoading(true, loadAnmodninger())
      //loadAnmodninger()
    }
    setIsLoading(false)
  }, [props.vendorMeta])

  async function loadAnmodninger() {
    // First get stuff from API
    setIsLoading(true)
    await getAnmodninger(props.vendorID)
    .then(response => {
      console.log(response)
      if (response.message === 'Der findes ingen anmodninger.') {
        setAnmodninger(defaultAnmodninger)
      } else {
        setAnmodninger(response.data)
      }
    })
    .catch(response => {
      enqueueSnackbar('Kunne ikke hente anmodninger: '+ response.messsage,{variant: 'error',})
      setIsError([true, response.messsage])
      setAnmodninger(defaultAnmodninger)
    })
    .finally(() => {
      setIsLoading(false)
    })
  }
  async function getAnmodninger(vendor_id) {
    let init = {
      headers: { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` }
    }
    return await API.post("API", "/Anmodninger/"+vendor_id, init);
  }

  async function refresh() {
    if (props.vendorID !== '') {
      await loadAnmodninger();
    }
  }

  async function openAnmodning(anmod_id) {
    history.push('/anmodning/' + anmod_id);
  }

  // Render
  return (
    <div className={containerClasses.root}>
      <AutoRefresh onRefresh={refresh} disabled={props.vendorID === ''} />
      <LoadingWrapper isLoading={isLoading}>
      <Grid
        container
        spacing={3}
        direction="column"
      >
        <Grid item>
          <Anmodninger
            anmodninger={anmodninger}
            props={props}
            loading={isLoading}
            error={isError}
            vendorMeta={props.vendorMeta}
          />
        </Grid>
        {activeAnmodninger.length > 0 && (
          <Grid item>
            <Card raised>
              <CardHeader title={`Aktive anmodninger (${activeAnmodninger.length})`} />
              <CardContent>
                <List>
                  {activeAnmodninger.map((anmod) => (
                    <ListItem 
                      key={anmod.anmod_id}
                      className={classes.listItem}
                      onClick={() => openAnmodning(anmod.anmod_id)}
                    >
                      <ListItemText
                        primary={`${anmod.graveforespoergselsnr || 'N/A'}/${anmod.interesseomraade || 'N/A'} - ${anmod.bemaerkning}`}
                      /><AnmodningStatusPill status={anmod.anmod_status} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}
        {completedAnmodninger.length > 0 && (
          <Grid item>
            <Card raised>
              <CardHeader title={`Gennemførte anmodninger, seneste (${completedAnmodninger.length})`} />
              <CardContent>
                <TextField
                  label="Søg graveforespørgselsnr eller bemærkning"
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Indtast graveforespørgselsnr eller bemærkning..."
                />
                <List className={classes.completedList}>
                  {filteredCompletedAnmodninger.map((anmod) => (
                    <ListItem 
                      key={anmod.anmod_id}
                      className={classes.listItem}
                      onClick={() => openAnmodning(anmod.anmod_id)}
                    >
                      <ListItemText
                        primary={`${anmod.graveforespoergselsnr || 'N/A'}/${anmod.interesseomraade || 'N/A'} - ${anmod.bemaerkning}`}
                      /><AnmodningStatusPill status={anmod.anmod_status} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      </LoadingWrapper>
    </div>
  );
}

